// TASK-74 SCH-A — pure helper for the Schemavy day-view auto-scroll-to-evening
// behavior. Extracted from LeagueCalendar.tsx so it can be unit-tested without
// jsdom + Radix scroll-area integration. The consumer wires this to a
// useRef'd ScrollArea viewport via a useEffect with a guard ref so the
// auto-scroll fires only once per (viewMode, day) — not on every re-render —
// and never fights user manual scrolling.

export interface CalculateEveningScrollPositionArgs {
  /** Match start times in "HH:MM" 24h format for matches in the current view. */
  matchTimes: string[];
  /** Hall opening hour (0–23) — the first column of the time axis. */
  hallStartHour: number;
  /** Pixel width of one 30-minute slot column. */
  slotWidth: number;
  /** Hour to scroll to when `matchTimes` is empty (e.g. 17 = 17:00). */
  fallbackHour: number;
  /**
   * Number of slot columns of lead-in context to show BEFORE the target slot.
   * For 30-minute slots, `2` shows a 1-hour lead-in. Negative values are
   * clamped to 0; the final scroll position is also clamped to ≥0 so a match
   * earlier than the hall opening (defensive) never produces a negative scroll.
   */
  leadInSlots: number;
}

function timeStringToMinutes(t: string): number {
  const parts = t.split(":");
  const h = Number(parts[0]) || 0;
  const m = Number(parts[1]) || 0;
  return h * 60 + m;
}

/**
 * Compute the `scrollLeft` (in pixels) that lands the day-view viewport just
 * before the earliest match (or `fallbackHour` when no matches exist),
 * leaving `leadInSlots` of context visible to the left of the first match.
 *
 * Always returns a non-negative integer.
 */
export function calculateEveningScrollPosition(
  args: CalculateEveningScrollPositionArgs,
): number {
  const { matchTimes, hallStartHour, slotWidth, fallbackHour, leadInSlots } = args;

  const earliestMin =
    matchTimes.length === 0
      ? fallbackHour * 60
      : Math.min(...matchTimes.map(timeStringToMinutes));

  const hallStartMin = hallStartHour * 60;
  const slotIndex = Math.floor((earliestMin - hallStartMin) / 30);
  const lead = Math.max(0, leadInSlots);
  const targetIndex = Math.max(0, slotIndex - lead);

  return Math.max(0, Math.floor(targetIndex * slotWidth));
}
